import React, { useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Row, Col, Button, Spinner, Table, Input } from 'reactstrap';
import { actions } from '../../../../../context';
import PracticeLocationItem from './PracticeLocationItem';
import PracticeLocationForm from './PracticeLocationForm';

const PAGE_SIZE_OPTIONS = [25, 50, 100];

const PracticeLocations = ({ practiceId }) => {
    const dispatch = useDispatch();
    const { locations, loading } = useSelector(
        (state) => state.practiceLocations,
        shallowEqual
    );

    const [showForm, setShowForm] = useState(false);
    const [editingLocation, setEditingLocation] = useState(null);
    const [includeDeleted, setIncludeDeleted] = useState(false);
    const [filter, setFilter] = useState('');
    const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
    const [page, setPage] = useState(1);

    useEffect(() => {
        if (practiceId) {
            dispatch(actions.practiceLocations.fetchPracticeLocations(practiceId, includeDeleted));
        }
    }, [dispatch, practiceId, includeDeleted]);

    // Filter by Location ID or Name (case-insensitive).
    const filteredLocations = useMemo(() => {
        const term = filter.trim().toLowerCase();
        if (!term) return locations;
        return locations.filter(
            (l) =>
                (l.locationId || '').toLowerCase().includes(term) ||
                (l.name || '').toLowerCase().includes(term)
        );
    }, [locations, filter]);

    const pageCount = Math.max(1, Math.ceil(filteredLocations.length / pageSize));
    const safePage = Math.min(Math.max(1, page), pageCount);
    const startIndex = (safePage - 1) * pageSize;
    const pagedLocations = filteredLocations.slice(startIndex, startIndex + pageSize);

    // Keep the clamped page in sync so pagination controls reflect reality
    // after filtering / page-size changes shrink the result set.
    useEffect(() => {
        if (page !== safePage) setPage(safePage);
    }, [page, safePage]);

    const handleEdit = (location) => {
        setEditingLocation(location);
        setShowForm(true);
    };

    const handleDelete = (practiceLocationId) => {
        dispatch(
            actions.practiceLocations.deletePracticeLocation(
                practiceLocationId,
                practiceId
            )
        );
    };

    const handleBillingPatch = (practiceLocationId, body) => {
        dispatch(
            actions.practiceLocations.patchLocationBilling(practiceLocationId, body)
        );
    };

    const handleAdd = () => {
        setEditingLocation(null);
        setShowForm(true);
    };

    const handleCancel = () => {
        setShowForm(false);
        setEditingLocation(null);
    };

    const handleSubmit = (values) => {
        if (editingLocation) {
            dispatch(
                actions.practiceLocations.updatePracticeLocation(
                    editingLocation.id,
                    values,
                    practiceId
                )
            );
        } else {
            dispatch(
                actions.practiceLocations.createPracticeLocation(
                    practiceId,
                    values
                )
            );
        }
        setShowForm(false);
        setEditingLocation(null);
    };

    return (
        <Row className="mb-4">
            <Col>
                <div className="d-flex justify-content-between border-bottom mb-2 pb-2">
                    <h5 className="op-7 mb-0">Practice Locations</h5>
                    <div className="d-flex align-items-center">
                        <label className="mr-3 mb-0 text-light" style={{ fontSize: '0.85rem', cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                className="mr-1"
                                checked={includeDeleted}
                                onChange={(e) => setIncludeDeleted(e.target.checked)}
                                aria-label="Show deleted locations"
                            />
                            Show deleted
                        </label>
                        <Button color="primary" size="sm" onClick={handleAdd}>
                            + Add
                        </Button>
                    </div>
                </div>
                {showForm ? (
                    <PracticeLocationForm
                        initialValues={editingLocation}
                        onSubmit={handleSubmit}
                        onCancel={handleCancel}
                    />
                ) : loading ? (
                    <div className="text-center py-3">
                        <Spinner color="light" />
                    </div>
                ) : locations.length === 0 ? (
                    <p>No locations configured for this practice.</p>
                ) : (
                    <>
                        <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap">
                            <Input
                                type="text"
                                bsSize="sm"
                                placeholder="Filter by Location ID or Name…"
                                value={filter}
                                onChange={(e) => {
                                    setFilter(e.target.value);
                                    setPage(1);
                                }}
                                aria-label="Filter locations"
                                style={{ maxWidth: '320px' }}
                            />
                            <div className="d-flex align-items-center text-light" style={{ fontSize: '0.85rem' }}>
                                <span className="mr-2">
                                    {filteredLocations.length === 0
                                        ? '0 results'
                                        : `${startIndex + 1}–${Math.min(startIndex + pageSize, filteredLocations.length)} of ${filteredLocations.length}`}
                                </span>
                                <Input
                                    type="select"
                                    bsSize="sm"
                                    value={pageSize}
                                    onChange={(e) => {
                                        setPageSize(Number(e.target.value));
                                        setPage(1);
                                    }}
                                    aria-label="Rows per page"
                                    style={{ width: 'auto' }}
                                >
                                    {PAGE_SIZE_OPTIONS.map((size) => (
                                        <option key={size} value={size}>
                                            {size} / page
                                        </option>
                                    ))}
                                </Input>
                            </div>
                        </div>
                        {filteredLocations.length === 0 ? (
                            <p>No locations match “{filter}”.</p>
                        ) : (
                            <>
                                <Table borderless size="sm" className="text-light">
                                    <thead>
                                        <tr>
                                            <th>Location ID</th>
                                            <th>Name</th>
                                            <th>Address</th>
                                            <th>Category</th>
                                            <th>Is Billable</th>
                                            <th>Operator-set</th>
                                            <th>Source</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pagedLocations.map((location) => (
                                            <PracticeLocationItem
                                                key={location.id}
                                                location={location}
                                                onEdit={handleEdit}
                                                onDelete={handleDelete}
                                                onBillingPatch={handleBillingPatch}
                                            />
                                        ))}
                                    </tbody>
                                </Table>
                                {pageCount > 1 && (
                                    <div className="d-flex justify-content-center align-items-center">
                                        <Button
                                            color="light"
                                            size="sm"
                                            outline
                                            onClick={() => setPage(1)}
                                            disabled={safePage === 1}
                                        >
                                            « First
                                        </Button>
                                        <Button
                                            color="light"
                                            size="sm"
                                            outline
                                            className="ml-2"
                                            onClick={() => setPage(safePage - 1)}
                                            disabled={safePage === 1}
                                        >
                                            ‹ Prev
                                        </Button>
                                        <span className="text-light mx-3" style={{ fontSize: '0.85rem' }}>
                                            Page {safePage} of {pageCount}
                                        </span>
                                        <Button
                                            color="light"
                                            size="sm"
                                            outline
                                            onClick={() => setPage(safePage + 1)}
                                            disabled={safePage === pageCount}
                                        >
                                            Next ›
                                        </Button>
                                        <Button
                                            color="light"
                                            size="sm"
                                            outline
                                            className="ml-2"
                                            onClick={() => setPage(pageCount)}
                                            disabled={safePage === pageCount}
                                        >
                                            Last »
                                        </Button>
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}
            </Col>
        </Row>
    );
};

export default PracticeLocations;
